import {get} from 'svelte/store';
import {combineLatest, BehaviorSubject, interval} from 'rxjs';
import {map, startWith, filter, shareReplay} from 'rxjs/operators';
import {fixLink$} from '@/store';
import {contractMarketsCache$} from '@/streams/market24h-contract';
import {contracts$, contractsCache$} from '@/streams/config/contract';
import {trends$} from '@/modules/markets/trends';
import {LINKS} from '@/const/link';
import {contractUtil} from '@/helper/contract';
import {timeUtil} from '@/helper/time';
import {spotMarketsCache$} from '@/streams/market24h-spot';
import {spots$, spotsCache$, marginCache$} from '@/streams/config/spot';
import {MarketSortSide} from '@/modules/markets/enum';
import {marketUtil} from '@/modules/markets/util2';
import {pilotCoinsChainCache$} from '@/streams/coin-info';

const fixLink = get(fixLink$);

export const searchValue$ = new BehaviorSubject<string>('');
export const sortParams$ = new BehaviorSubject({key: 'combinationTurnoverEv', side: 'Normal'});

export const contractMarkets$ = combineLatest([contractMarketsCache$, contractsCache$, trends$]).pipe(
  map(([contractMarketsCache, contractsCache, trends]) => {
    const markets = [...contractMarketsCache.values()];
    return markets
      .filter(({status}) => marketUtil.checkTradePairVisible(status))
      .map((market) => {
        const contract = contractsCache.get(market.symbol);
        const {type, symbol, symbolV2, urlSymbol, displaySymbol, status, settleCurrency, settleCurrencyDisplay, baseCurrency, quoteCurrency, contractSide, categoryIds, timeline, originType} =
          contract;
        const trend = trends[market.symbol];
        const pathKey = contractSide > 0 ? (contractUtil.isPilotContract(originType) ? 'PHEMEX_PILOT_CONTRACT_TRADE' : 'PHEMEX_CONTRACT_TRADE') : 'PHEMEX_CONTRACT_INVERSE_TRADE';
        const tradeLink = fixLink(`${LINKS[pathKey]}/${urlSymbol}`);
        return {
          ...market,
          klineData: trend?.prices || [],
          type,
          symbol,
          symbolV2,
          displaySymbol,
          settleCurrencyDisplay,
          tradeLink,
          status,
          settleCurrency,
          baseCurrency,
          quoteCurrency,
          contractSide,
          listTime: timeline[1],
          listTimeStr: timeUtil.formatUTCDateReverse(timeline[1]),
          categoryIds,
          timeline,
          volumeEv: market.volume,
          urlSymbol,
        };
      });
  }),
  startWith([]),
  shareReplay(1)
);

export const spotMarkets$ = combineLatest([spotMarketsCache$, spotsCache$, trends$]).pipe(
  map(([spotMarketsCache, spotsCache, trends]) => {
    const markets = [...spotMarketsCache.values()];
    return markets
      .filter(({status}) => marketUtil.checkTradePairVisible(status))
      .map((market) => {
        const spot = spotsCache.get(market.symbol);
        const {name, displaySymbol, urlSymbol, baseCurrency, displayBaseCurrency, quoteCurrency, status, type, baseCurrencyName, categoryIds, timeline, stStatus} = spot;
        const tradeLink = fixLink(LINKS.SPOT_TRADE + '/' + urlSymbol);
        const trend = trends[market.symbol];
        return {
          ...market,
          klineData: trend?.prices || [],
          pairName: displaySymbol,
          type,
          name,
          displaySymbol,
          baseCurrency,
          displayBaseCurrency,
          tradeLink,
          quoteCurrency,
          status,
          listTime: timeline[1],
          listTimeStr: timeUtil.formatUTCDateReverse(timeline[1]),
          baseCurrencyName,
          categoryIds,
          timeline,
          stStatus,
          urlSymbol,
        };
      });
  }),
  startWith([]),
  shareReplay(1)
);

export const marginMarkets$ = combineLatest([spotMarkets$, marginCache$]).pipe(
  filter(([spotMarkets]) => spotMarkets.length > 0),
  map(([spotMarkets, marginsMap]) =>
    spotMarkets
      .map((market) => {
        const tradeLink = fixLink(LINKS.PHEMEX_SPOT_MARGIN_TRADE + '/' + market.urlSymbol);
        return {...market, tradeLink};
      })
      .filter((market) => [...marginsMap.keys()].includes(market.symbol))
  ),
  startWith([]),
  shareReplay(1)
);

export const rawNewListingMarkets$ = combineLatest([contractMarkets$, spotMarkets$]).pipe(
  map(([contractMarkets, spotMarkets]) => {
    const markets = marketUtil.sortWithListTime([...contractMarkets, ...spotMarkets]);
    return markets.slice(0, 10);
  }),
  shareReplay(1)
);

export const newListingMarkets$ = combineLatest([searchValue$, sortParams$, rawNewListingMarkets$]).pipe(
  map(([searchValue, params, markets]) => {
    const value = searchValue && searchValue.toUpperCase();
    return markets.filter(({symbol}) => symbol.includes(value)).sort(marketUtil.compare(params));
  })
);

export const pilotContractMarkets$ = combineLatest([contractMarkets$, pilotCoinsChainCache$]).pipe(
  map(([o, coinsChainCache]) => {
    return o
      .filter((market) => contractUtil.isPilotContract(market.originType))
      .map((market) => {
        const info = (coinsChainCache && coinsChainCache.get(market.baseCurrency)) || {shortAddress: '', marketCapEv: 0, marketCap: '', chainName: ''};
        return {...market, address: info.shortAddress, marketCapEv: info.marketCapEv, marketCap: info.marketCap, chainName: info.chainName};
      });
  }),
  startWith([]),
  shareReplay(1)
);

export const rawComingSoon$ = combineLatest([contracts$, spots$, interval(1000)]).pipe(
  map(([contracts, spots]) => {
    const pairs = [...contracts, ...spots];
    return pairs
      .filter(({status}) => status === 'PreList')
      .map((item) => {
        const [_, listTime] = item.timeline || [0, 0];
        const curMs = Date.now();
        const d = Math.floor((listTime - curMs) / 86400000);
        const tenDay = Math.floor(d / 10);
        const day = d % 10;
        const time = timeUtil.formatUTCTime(listTime - curMs);
        const countdown = !!tenDay || !!day ? tenDay.toString() + day + ' Days ' + time : time;
        return {...item, time: timeUtil.formatDateTime(listTime), countdown};
      });
  }),
  startWith([])
);

export const comingSoon$ = combineLatest([searchValue$, sortParams$, rawComingSoon$]).pipe(
  map(([searchValue, params, pairs]) => {
    const value = searchValue && searchValue.toUpperCase();
    return pairs
      .filter(({symbol}) => symbol.includes(value))
      .sort((a, b) => {
        if (params && params.key === 'symbol') {
          return params.side === MarketSortSide.Down ? b.symbol.localeCompare(a.symbol) : a.symbol.localeCompare(b.symbol);
        }
        return a.timeline[1] - b.timeline[1];
      });
  }),
  startWith([])
);
